import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Truck, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError, walletBrand } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";

/**
 * Çatdırılma haqqının ödənişi.
 *
 * Kuryerin qiyməti məsafədən asılıdır, ona görə əvvəlcədən bilinmir.
 * Sahibkar məbləği admin paneldə yazır, müştəriyə bu səhifənin linkini
 * göndərir. Səhifə yalnız gizli açarla açılır — link olmadan kimsə
 * başqasının sifarişini görə bilməz.
 */
const DeliveryPayment = () => {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { t } = useLanguage();

  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [brand] = useState(walletBrand);

  const load = useCallback(() => {
    api.get(`/delivery/${orderNumber}`, { params: { token } })
      .then(({ data }) => setInfo(data))
      .catch(() => setNotFound(true));
  }, [orderNumber, token]);

  useEffect(() => { load(); }, [load]);

  /* Apple/Google Pay düyməsi səhifə açılan kimi görünməlidir.
     Əvvəl o, yalnız "Ödəniş et"ə basandan sonra çıxırdı — müştəri isə
     ekranda yalnız kart seçimini görüb elə bilirdi ki, başqa yol yoxdur. */
  useEffect(() => {
    if (!info || info.status !== "pending" || wallet) return;
    let alive = true;
    api.get(`/delivery/${orderNumber}/wallet`, { params: { token } })
      .then(({ data }) => {
        if (alive && data.available && data.widget_url) {
          setWallet({ url: data.widget_url, origin: data.origin });
        }
      })
      .catch(() => { /* cüzdan yoxdursa kart yolu onsuz da açıqdır */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info?.status]);

  /* Ödəniş pəncərəsindən gələn mesaj.
     ⚠️ Yalnız epoint-in ünvanından qəbul edilir. Onsuz da bu mesaj ödənişi
     təsdiqləmir — real status serverdən yenidən soruşulur. */
  useEffect(() => {
    if (!wallet) return;
    const onMsg = (e) => {
      if (e.origin !== wallet.origin) return;
      let d = e.data;
      if (typeof d === "string") { try { d = JSON.parse(d); } catch { return; } }
      if (d?.status === "success") {
        setWallet(null);
        setInfo((p) => ({ ...p, status: "checking" }));
        // Serverin təsdiqi bir neçə saniyə çəkə bilər.
        setTimeout(load, 2500);
      } else if (["error", "failed", "declined"].includes(d?.status)) {
        setWallet(null);
        toast.error(t("pay_fail_title"));
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [wallet, load, t]);

  const payByCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/delivery/${orderNumber}/pay`, null,
        { params: { token } });
      window.location.href = data.redirect_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="max-w-lg mx-auto px-6 py-32 text-center" data-testid="delivery-not-found">
        <h1 className="font-serif font-light text-3xl">{t("delivery_link_bad")}</h1>
        <p className="mt-4 text-brand-sub font-light">{t("delivery_link_bad_sub")}</p>
      </div>
    );
  }

  if (!info) {
    return <div className="max-w-lg mx-auto px-6 py-32 text-center text-brand-muted">{t("loading")}</div>;
  }

  if (info.status === "paid") {
    return (
      <div className="max-w-lg mx-auto px-6 py-32 text-center" data-testid="delivery-paid">
        <CheckCircle2 size={48} strokeWidth={1} className="mx-auto text-brand-ink" />
        <h1 className="font-serif font-light text-3xl mt-8">{t("delivery_paid_title")}</h1>
        <p className="mt-4 text-brand-sub font-light">{t("delivery_paid_sub")}</p>
        <p className="mt-8 font-mono text-sm text-brand-muted">{info.order_number}</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-6 py-20" data-testid="delivery-page">
      <div className="text-center">
        <Truck size={40} strokeWidth={1} className="mx-auto text-brand-muted" />
        <h1 className="font-serif font-light text-3xl tracking-tight mt-6">
          {t("delivery_title")}
        </h1>
        <p className="mt-3 text-sm text-brand-sub">
          {info.customer_name ? `${info.customer_name} · ` : ""}
          <span className="font-mono">{info.order_number}</span>
        </p>
      </div>

      <div className="border border-brand-line mt-10 p-8 text-center">
        <p className="overline">{t("delivery_amount")}</p>
        <p className="font-serif text-5xl mt-3" data-testid="delivery-amount">
          {formatPrice(info.amount)}
        </p>
        <p className="mt-4 text-xs text-brand-muted">{t("delivery_note")}</p>
      </div>

      {info.status === "checking" ? (
        <div className="mt-8 flex flex-col items-center gap-3">
          <Loader2 size={24} strokeWidth={1.5} className="animate-spin text-brand-muted" />
          <p className="text-sm text-brand-sub">{t("pay_pending_title")}</p>
        </div>
      ) : (
        <div className="mt-8">
          {/* Cüzdan varsa onun düyməsi ən yuxarıdadır — bir toxunuşla ödəniş
              kart nömrəsi yazmaqdan asandır. Cihazda cüzdan yoxdursa epoint-in
              çərçivəsi boş qalır, ona görə kart yolu HƏMİŞƏ görünür. */}
          {wallet && (
            <>
              <iframe
                src={wallet.url}
                title={brand}
                className="w-full block"
                data-testid="delivery-wallet"
                style={{ height: 64, border: 0 }}
                allow="payment"
              />
              <div className="flex items-center gap-3 my-4">
                <span className="h-px flex-1 bg-brand-line" />
                <span className="text-xs text-brand-muted uppercase tracking-wider">{t("or")}</span>
                <span className="h-px flex-1 bg-brand-line" />
              </div>
            </>
          )}

          <button
            type="button"
            onClick={payByCard}
            disabled={busy}
            data-testid="delivery-pay-btn"
            className={`w-full h-[52px] rounded-[10px] text-sm tracking-[0.15em] uppercase transition-colors disabled:opacity-50 ${
              wallet
                ? "border border-brand-ink hover:bg-brand-bg2"
                : "bg-brand-ink text-white hover:bg-brand-oxid"
            }`}
          >
            {busy ? t("loading") : wallet ? t("pay_with_card") : t("pay_now")}
          </button>

          {!wallet && (
            <p className="mt-3 text-xs text-brand-muted text-center">
              {brand} · Visa · Mastercard
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveryPayment;
